import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Undo2 } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
  action?: NotificationAction;
}

interface NotificationContextType {
  showNotification: (type: NotificationType, message: string, duration?: number, action?: NotificationAction) => void;
  success: (message: string, duration?: number, action?: NotificationAction) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  showUndoNotification: (message: string, onUndo: () => void, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (type: NotificationType, message: string, duration = 4000, action?: NotificationAction) => {
      const id = `${Date.now()}-${Math.random()}`;
      const notification: Notification = { id, type, message, duration, action };

      setNotifications((prev) => [...prev, notification]);

      if (duration > 0) {
        setTimeout(() => {
          removeNotification(id);
        }, duration);
      }
    },
    [removeNotification]
  );

  const success = useCallback((message: string, duration?: number, action?: NotificationAction) => {
    showNotification('success', message, duration, action);
  }, [showNotification]);

  const error = useCallback((message: string, duration?: number) => {
    showNotification('error', message, duration);
  }, [showNotification]);

  const info = useCallback((message: string, duration?: number) => {
    showNotification('info', message, duration);
  }, [showNotification]);

  const warning = useCallback((message: string, duration?: number) => {
    showNotification('warning', message, duration);
  }, [showNotification]);

  const showUndoNotification = useCallback((message: string, onUndo: () => void, duration = 8000) => {
    showNotification('info', message, duration, {
      label: 'Undo',
      onClick: onUndo,
      icon: <Undo2 className="h-4 w-4" />
    });
  }, [showNotification]);

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
      case 'error':
        return <AlertCircle className="h-5 w-5" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      case 'info':
        return <Info className="h-5 w-5" />;
    }
  };

  const getStyles = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200';
      case 'info':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200';
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification, success, error, info, warning, showUndoNotification }}>
      {children}

      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg border-2 shadow-lg backdrop-blur-sm animate-slide-left min-w-[300px] max-w-md ${getStyles(
              notification.type
            )}`}
          >
            <div className="flex-shrink-0">{getIcon(notification.type)}</div>
            <p className="flex-1 text-sm font-medium">{notification.message}</p>
            {notification.action && (
              <button
                onClick={() => {
                  notification.action?.onClick();
                  removeNotification(notification.id);
                }}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/20 hover:bg-white/30 dark:bg-gray-700/50 dark:hover:bg-gray-700/70 text-sm font-semibold transition-colors"
              >
                {notification.action.icon}
                {notification.action.label}
              </button>
            )}
            <button
              onClick={() => removeNotification(notification.id)}
              className="flex-shrink-0 hover:opacity-70 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
